'use client';

import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import CurrencyIcon from '@/components/ui/icons/currency-icon';
import { handlePriceByComma } from '@/helpers/common';
import { addMultiItemsToCart } from '@/services/cart';
import { setCookie, getCookies } from 'cookies-next';
import useCheckout from '@/store/checkout.store';
import useCart from '@/store/cart.store';
import { toast } from '@/utils/toast';
import { ProductListProvider } from '@/components/product-list-context';
import { PRODUCT_LISTS } from '@/utils/gtm/item-lists';
import BundleProductItem from './BundleProductItem';

const FrequentlyBoughtTogether = ({ bundle_discounts, product }) => {
  const t = useTranslations('product');
  const locale = useLocale();
  const cart = useCart((store) => store);
  const clearCheckout = useCheckout((state) => state.clearCheckout);
  const childProducts = Object.values(
    bundle_discounts?.child_details || {}
  ).slice(0, 2);

  const allProducts = useMemo(() => {
    return [
      {
        ...product,
        isMain: true,
        price: product?.prices_with_tax?.price || 0,
        special_price: product?.prices_with_tax?.special_price || null,
      },
      ...childProducts,
    ];
  }, [product, childProducts]);

  // Always select the main product by default
  const [selectedItems, setSelectedItems] = useState(
    allProducts?.filter((e) => e?.isMain)
  );

  const [isLoading, setIsLoading] = useState(false);

  const toggleItem = (item, isMain) => {
    if (isMain) return;
    setSelectedItems((prev) => {
      const newSelection = prev?.some((e) => e?.sku === item?.sku)
        ? prev?.filter((e) => e?.sku !== item?.sku)
        : [...prev, item];

      // Always keep main(s) included
      const mains = allProducts?.filter((e) => e?.isMain);
      return [...mains, ...newSelection?.filter((e) => !e?.isMain)];
    });
  };

  const selectedSkus = useMemo(() => {
    return selectedItems?.map((e) => e?.sku);
  }, [selectedItems]);

  const totalDiscountedAmount = useMemo(() => {
    return allProducts?.reduce((total, p) => {
      if (!selectedSkus?.includes(p?.sku)) return total;
      return (
        total +
        (+p?.special_price || +p?.price || 0) -
        (+p?.discount_amount || 0)
      );
    }, 0);

    // eslint-disable-next-line
  }, [selectedSkus]);

  const totalOriginalAmount = useMemo(() => {
    return allProducts?.reduce((total, p) => {
      if (!selectedSkus?.includes(p?.sku)) return total;
      return total + (+p?.price || 0);
    }, 0);
    // eslint-disable-next-line
  }, [selectedSkus]);

  const handleAddProductsBundle = async () => {
    setIsLoading(true);
    const { quote, mage } = getCookies();

    try {
      const res = await addMultiItemsToCart(
        mage,
        quote,
        selectedItems?.map((e) => {
          return {
            sku: e?.sku,
            qty: 1,
          };
        })
      );

      if (res?.status === 200) {
        // Handle new quote
        if (quote !== res?.data?.quoteId) {
          cart.resetCart();
          clearCheckout();
          setCookie('quote', res?.data?.quoteId);
        }

        const mergedItems = (selectedItems || []).map((product) => {
          const responseItem = (res?.data?.data || []).find(
            (r) => r.sku === product?.sku
          );

          return {
            ...product,
            ...responseItem,
            qty: responseItem?.qty ?? product.qty ?? 1,
            productFeesFromCart: [],
            relatedProducts: [],
          };
        });

        cart?.addItems(mergedItems);
        cart?.openCartPopup();
        setIsLoading(false);
      } else {
        toast.error(
          t(res?.error?.error?.message?.replaceAll('.', '') || 'ErrorAddToCart')
        );
        setIsLoading(false);
      }
    } catch (e) {
      toast.error(t('ErrorAddToCart'));
      console.error(e);
      setIsLoading(false);
    }
  };

  return (
    <ProductListProvider list={PRODUCT_LISTS.pdpBundle}>
      <div className='my-5 border-t border-gray-100 py-6 sm:my-7 lg:my-10'>
        <h2 className='mb-4 text-xl font-bold'>
          {locale === 'en' ? 'Frequently Bought Together' : 'يتم شراؤهم معا'}
        </h2>
        {/* Mobile view */}
        <div className='overflow-hidden rounded-lg border border-gray-200 shadow-sm lg:hidden'>
          <div className='divide-y divide-gray-200'>
            {allProducts.map((p, index) => (
              <BundleProductItem
                key={p.sku}
                p={p}
                selectedItems={selectedItems}
                toggleItem={toggleItem}
                index={index}
              />
            ))}
          </div>
          <div className='flex items-center justify-between border-t border-gray-200 bg-gray-50 p-4'>
            <div className='flex flex-col'>
              <span className='mb-1 text-sm font-medium text-gray-600'>
                {locale === 'en' ? 'Total Amount' : 'الإجمالى'}
              </span>
              <div className='flex flex-col'>
                {totalOriginalAmount !== totalDiscountedAmount && (
                  <span className='mb-1 flex items-center gap-1 text-sm text-gray-500 line-through'>
                    {handlePriceByComma(+totalOriginalAmount)}{' '}
                    <CurrencyIcon className='size-3' />
                  </span>
                )}
                <span className='flex items-center gap-1 text-base font-bold text-red-600'>
                  {handlePriceByComma(+totalDiscountedAmount)}{' '}
                  <CurrencyIcon className='size-3' />
                </span>
              </div>
            </div>
            <button
              onClick={handleAddProductsBundle}
              className='rounded-md border border-[#1a2650] bg-white px-5 py-2.5 font-medium text-[#1a2650] shadow-sm transition-colors hover:bg-[#f8f9ff]'
            >
              {' '}
              {locale === 'en' ? 'Add All to Cart' : 'أضف الكل للعربة'}{' '}
            </button>
          </div>
        </div>

        {/* Desktop view */}
        <div className='hidden overflow-hidden rounded-lg border border-gray-200 lg:block'>
          <div className='grid grid-cols-3 divide-x divide-gray-200'>
            {allProducts?.map((p, index) => (
              <BundleProductItem
                key={p?.sku}
                p={p}
                selectedItems={selectedItems}
                toggleItem={toggleItem}
                index={index}
              />
            ))}
          </div>

          <div className='flex items-center justify-between border-t border-gray-200 bg-gray-50 p-5'>
            <div>
              <div className='mb-1 text-sm font-medium'>
                {locale === 'en' ? 'Total Amount' : 'الإجمالى'}
              </div>
              <div className='flex flex-col'>
                {totalOriginalAmount !== totalDiscountedAmount && (
                  <div className='mb-1 flex items-center gap-1 text-sm text-gray-500 line-through'>
                    {handlePriceByComma(+totalOriginalAmount)}{' '}
                    <CurrencyIcon className='size-3' />
                  </div>
                )}
                <div className='flex items-center gap-1 text-base font-bold text-red-600'>
                  {handlePriceByComma(+totalDiscountedAmount)}{' '}
                  <CurrencyIcon className='size-3' />
                </div>
              </div>
            </div>
            <button
              onClick={handleAddProductsBundle}
              disabled={isLoading}
              className='flex items-center gap-2 rounded border border-[#1a2650] bg-white px-6 py-2.5 font-medium text-[#1a2650] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed'
            >
              {' '}
              {isLoading ? (
                <span className='size-4 animate-spin rounded-full border-t border-r border-[#1a2650] py-1' />
              ) : null}
              {locale === 'en' ? 'Add All to Cart' : 'أضف الكل للعربة'}{' '}
            </button>
          </div>
        </div>
      </div>
    </ProductListProvider>
  );
};

export default FrequentlyBoughtTogether;
